import { useState, useEffect } from 'react'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { supabase } from './lib/supabase'
import AuthPage from './pages/AuthPage'
import AppShell from './pages/AppShell'
import Onboarding from './pages/Onboarding'
import ResetPasswordPage from './pages/ResetPasswordPage'
import SetPasswordPage from './pages/SetPasswordPage'
import AccessRequiredPage from './pages/AccessRequiredPage'
import { colors, fonts } from './components/UI'
import { ThemeProvider, useTheme } from './lib/theme'

function AppInner() {
  const { user, profile, loading, signOut } = useAuth()
  const { applyExternal } = useTheme()
  const [onboardingDone, setOnboardingDone] = useState(null)
  const [isPasswordReset, setIsPasswordReset] = useState(false)
  const [pwSetLocal, setPwSetLocal] = useState(false)
  const [access, setAccess] = useState(null) // null = checking, true, false

  // Account-level flag: has this identity ever set a password? Set once on first
  // sign-in (here or anywhere else sharing the TIC identity). pwSetLocal is an
  // immediate fallback so the set-password screen advances without waiting on the
  // USER_UPDATED event to round-trip.
  const passwordSet = pwSetLocal || (user?.user_metadata?.password_set === true)

  // Detect the password-recovery flow from the URL hash and from auth events.
  useEffect(() => {
    const hash = window.location.hash
    if (hash.includes('type=recovery')) setIsPasswordReset(true)

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setIsPasswordReset(true)
      if (event === 'USER_UPDATED') setIsPasswordReset(false)
      if (event === 'SIGNED_OUT') { setAccess(null); setOnboardingDone(null); setPwSetLocal(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  // Reset per-user gates whenever the user changes or signs out.
  useEffect(() => {
    if (!user) { setAccess(null); setOnboardingDone(null); setPwSetLocal(false) }
  }, [user])

  // Cross-device theme: adopt the theme saved on the profile once it loads.
  useEffect(() => {
    if (profile && profile.theme) applyExternal(profile.theme)
  }, [profile])

  // Entitlement gate: once signed in with a password set, ask the server (service
  // role, via the verified token) whether this account's purchase grants Tracker
  // access. Fails closed.
  useEffect(() => {
    if (!user || !passwordSet || isPasswordReset || access !== null) return
    let cancelled = false
    async function check() {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const token = session?.access_token
        if (!token) { if (!cancelled) setAccess(false); return }
        const res = await fetch('/.netlify/functions/check-access', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        })
        const data = await res.json()
        if (!cancelled) setAccess(!!data.access)
      } catch {
        if (!cancelled) setAccess(false)
      }
    }
    check()
    return () => { cancelled = true }
  }, [user, passwordSet, isPasswordReset, access])

  // Onboarding check - only once the user is confirmed entitled.
  useEffect(() => {
    if (!user || access !== true) return
    async function checkOnboarding() {
      // Consider the user set up if they've completed onboarding, OR have any
      // real data (accounts or logged income). Belt-and-braces so nobody who's
      // genuinely set up ever gets bounced back into onboarding.
      const { data: accounts } = await supabase.from('accounts').select('id').eq('user_id', user.id).limit(1)
      const { data: income } = await supabase.from('income_entries').select('id').eq('user_id', user.id).limit(1)
      const { data: profile } = await supabase.from('profiles').select('onboarding_done').eq('id', user.id).single()
      const hasData = (profile && profile.onboarding_done) || (accounts && accounts.length > 0) || (income && income.length > 0)
      setOnboardingDone(hasData)
    }
    checkOnboarding()
  }, [user, access])

  const LoadingScreen = () => (
    <div style={{ height: '100vh', height: '100dvh', background: colors.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '44px', background: colors.gold }} />
        <div>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '18px', color: colors.text, letterSpacing: '0.04em', lineHeight: 1 }}>INVESTING</div>
          <div style={{ fontFamily: fonts.heading, fontWeight: 300, fontSize: '12px', color: colors.gold, letterSpacing: '0.18em' }}>COUPLE</div>
        </div>
      </div>
      <div style={{ fontSize: '10px', color: `rgba(${colors.textRgb},0.5)`, fontFamily: fonts.body, letterSpacing: '0.04em', }}>Budget Tracker</div>
    </div>
  )

  // Initial auth resolution.
  if (loading) return <LoadingScreen />

  // Password-recovery flow - show the reset page regardless of auth state.
  if (isPasswordReset) {
    return <ResetPasswordPage onDone={() => { setIsPasswordReset(false) }} />
  }

  // Not signed in.
  if (!user) return <AuthPage />

  // First sign-in on this identity: set a password once.
  if (!passwordSet) {
    return <SetPasswordPage onDone={() => setPwSetLocal(true)} />
  }

  // Entitlement still resolving.
  if (access === null) return <LoadingScreen />

  // Signed in, but this account has not bought anything that grants the Tracker.
  if (access === false) {
    return <AccessRequiredPage email={user.email} onSignOut={async () => { await signOut() }} />
  }

  // Entitled - onboarding gate, then the app.
  if (onboardingDone === null) return <LoadingScreen />
  if (!onboardingDone) return <Onboarding onComplete={() => setOnboardingDone(true)} />
  return <AppShell />
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppInner />
      </ThemeProvider>
    </AuthProvider>
  )
}
